import {Alert} from 'src/design/atoms/Alert';
import {Text} from 'src/design/atoms/Text';
import type {EndEvent, TimeUpdateEvent} from '@u-wave/react-vimeo';
import VimeoPlayer from '@u-wave/react-vimeo';
import {useCallback, useRef, useState} from 'react';
import {useFlushOnHide} from 'src/shared/hooks/useFlushOnHide';
import {reportVideoError} from 'src/shared/utils/videoTelemetry';

export interface VimeoVideoPlayerProps {
  videoUrl: string;
  enableVideoAutostart: boolean;
  enableVideoResume?: boolean;
  isVideoPaused?: boolean;
  currentProgressSeconds?: number;
  onVideoTimeEventUpdate?: (seconds: number) => void;
  onVideoEnd?: (seconds: number) => void;
}

export const VimeoVideoPlayer: React.FC<VimeoVideoPlayerProps> = ({
  videoUrl,
  enableVideoResume,
  enableVideoAutostart,
  isVideoPaused,
  currentProgressSeconds,
  onVideoTimeEventUpdate,
  onVideoEnd,
}) => {
  const [hasError, setHasError] = useState(false);

  // If enableVideoResume is enabled, we'll track the video progress so we can resume it later.
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const latestSecondsRef = useRef<number>(0);

  const handleTimeUpdate = (event: TimeUpdateEvent) => {
    const {seconds} = event;
    latestSecondsRef.current = seconds;

    // We only care about 2 second intervals.
    // So floor the current time and compare to previous time + 4, which should be a 5 second difference.
    if (Math.floor(seconds) > playedSeconds + 4) {
      setPlayedSeconds(seconds);

      if (onVideoTimeEventUpdate) {
        onVideoTimeEventUpdate(seconds);
      }
    }
  };
  const handleVideoEnd = (event: EndEvent) => {
    const {seconds} = event;
    if (onVideoEnd) {
      onVideoEnd(seconds);
    }
  };

  const onVideoError = (error: unknown) => {
    setHasError(true);
    reportVideoError('vimeo', error);
  };

  const getFlushValue = useCallback(() => latestSecondsRef.current, []);
  const handleFlush = useCallback(
    (s: number) => {
      if (enableVideoResume && onVideoTimeEventUpdate)
        onVideoTimeEventUpdate(s);
    },
    [enableVideoResume, onVideoTimeEventUpdate]
  );
  useFlushOnHide(getFlushValue, handleFlush);

  if (hasError) {
    return (
      <Alert color="red" title="Video playback error" variant="light">
        <Text size="sm">
          This video could not be played. Please reload the page and try again.
        </Text>
      </Alert>
    );
  }

  return (
    <VimeoPlayer
      responsive={true}
      video={videoUrl}
      muted={true}
      autoplay={enableVideoAutostart}
      start={currentProgressSeconds ?? 0}
      loop={false}
      paused={isVideoPaused}
      onError={onVideoError}
      onTimeUpdate={enableVideoResume ? handleTimeUpdate : undefined}
      onEnd={onVideoEnd ? handleVideoEnd : undefined}
    />
  );
};
